import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { trainersAPI } from '../services/api';
import { MICROSOFT_TECH_TAGS, COMMON_CERT_CODES } from '../constants/trainerTags';
import { SOLUTION_AREAS } from '../constants/options';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Plus, Search, Mail, Phone, Globe, Star, CalendarDays, MoreHorizontal, Edit2, UserX, X, User, Award, Tag, Upload, Download, TrendingUp, Bookmark } from 'lucide-react';
import { toast } from 'sonner';
import { TablePagination, PAGE_SIZE } from '../components/shared/TablePagination';
import { ColumnFilterPopover } from '../components/shared/ColumnFilterPopover';
import { ActiveFiltersBar } from '../components/shared/ActiveFiltersBar';

const COUNTRIES = ['India','United States','United Kingdom','Australia','Canada','Singapore','UAE','Germany','France','Netherlands','Sweden','Japan','South Korea','Brazil','South Africa','Kenya','Nigeria','Malaysia','Indonesia','Philippines','New Zealand','Other'];
const EMPTY_FORM = { name:'',email:'',phone:'',country:'',timezone:'',expertise_tags:[],certifications:[],solution_areas:[],bio:'',is_featured:false };

function StarRating({ value }) {
  const stars = [1, 2, 3, 4, 5];
  return (
    <div className="flex items-center gap-0.5">
      {stars.map(i => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= Math.round(value || 0) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200 fill-gray-200'}`} />
      ))}
      {value && <span className="text-xs text-muted-foreground ml-1">{value.toFixed(1)}</span>}
    </div>
  );
}

export const TrainersPage = () => {
  const navigate = useNavigate();
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [colFilters, setColFilters] = useState({ country: new Set(), solution_area: new Set() });
  const setColFilter = (k, s) => setColFilters(prev => ({ ...prev, [k]: s }));
  const [highlightFilter, setHighlightFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [certInput, setCertInput] = useState('');
  const [trainersPage, setTrainersPage] = useState(1);

  const loadTrainers = useCallback(async () => {
    try {
      setLoading(true);
      setTrainers(await trainersAPI.getAll());
    } catch {
      toast.error('Failed to load trainers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTrainers(); }, [loadTrainers]);

  const openAddDialog = () => { setEditing(null); setForm(EMPTY_FORM); setTagInput(''); setCertInput(''); setDialogOpen(true); };
  const openEditDialog = (t) => {
    setEditing(t);
    setForm({ name:t.name||'',email:t.email||'',phone:t.phone||'',country:t.country||'',timezone:t.timezone||'',expertise_tags:t.expertise_tags||[],certifications:t.certifications||[],solution_areas:t.solution_areas||[],bio:t.bio||'',is_featured:t.is_featured||false });
    setDialogOpen(true);
  };

  const addTag = (tag) => {
    const t = tag.trim();
    if (t && !form.expertise_tags.includes(t)) setForm(f => ({ ...f, expertise_tags: [...f.expertise_tags, t] }));
    setTagInput('');
  };
  const removeTag = (tag) => setForm(f => ({ ...f, expertise_tags: f.expertise_tags.filter(t => t !== tag) }));

  const addCert = (cert) => {
    const c = cert.trim().toUpperCase();
    if (c && !form.certifications.includes(c)) setForm(f => ({ ...f, certifications: [...f.certifications, c] }));
    setCertInput('');
  };
  const removeCert = (cert) => setForm(f => ({ ...f, certifications: f.certifications.filter(c => c !== cert) }));

  const toggleSolutionArea = (area) => setForm(f => ({
    ...f,
    solution_areas: f.solution_areas.includes(area) ? f.solution_areas.filter(a => a !== area) : [...f.solution_areas, area],
  }));

  const handleCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportLoading(true);
    try {
      const result = await trainersAPI.bulkImportCsv(file);
      toast.success(result.message);
      loadTrainers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Import failed');
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.name || !form.email) { toast.error('Name and email are required'); return; }
    setSaving(true);
    try {
      if (editing) { await trainersAPI.update(editing.id, form); toast.success('Trainer updated'); }
      else { await trainersAPI.create(form); toast.success('Trainer added'); }
      setDialogOpen(false);
      loadTrainers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save trainer');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (trainer) => {
    if (!window.confirm(`Deactivate ${trainer.name}?`)) return;
    try { await trainersAPI.deactivate(trainer.id); toast.success('Trainer deactivated'); loadTrainers(); }
    catch { toast.error('Failed to deactivate trainer'); }
  };

  const handleToggleFeatured = async (trainer) => {
    try {
      await trainersAPI.update(trainer.id, { is_featured: !trainer.is_featured });
      toast.success(trainer.is_featured ? 'Removed from Featured' : 'Marked as Featured');
      loadTrainers();
    } catch { toast.error('Failed to update trainer'); }
  };

  // Pre-compute filtered data (avoid chained .filter().map() in JSX)
  const filtered = trainers
    .filter(t => {
      const s = search.toLowerCase();
      const matchSearch = !search || t.name?.toLowerCase().includes(s) || t.email?.toLowerCase().includes(s) || (t.expertise_tags || []).some(tag => tag.toLowerCase().includes(s));
      const matchCountry = !colFilters.country.size || colFilters.country.has(t.country);
      const matchSolution = !colFilters.solution_area.size || (t.solution_areas || []).some(a => colFilters.solution_area.has(a));
      const matchHighlight =
        highlightFilter === 'all' ? true :
        highlightFilter === 'featured' ? !!t.is_featured :
        highlightFilter === 'top_rated' ? t.avg_rating != null :
        highlightFilter === 'most_utilized' ? (t.total_sessions || 0) > 0 : true;
      return matchSearch && matchCountry && matchSolution && matchHighlight;
    })
    .sort((a, b) => {
      if (highlightFilter === 'top_rated') return (b.avg_rating || 0) - (a.avg_rating || 0);
      if (highlightFilter === 'most_utilized') return (b.total_sessions || 0) - (a.total_sessions || 0);
      return 0;
    });

  const uniqueCountries = [...new Set(trainers.map(t => t.country).filter(Boolean))].sort();
  const countryOpts = uniqueCountries.map(c => ({ value: c, label: c }));
  const solutionAreaOpts = [...new Set(trainers.flatMap(t => t.solution_areas || []).filter(Boolean))].sort().map(a => ({ value: a, label: (SOLUTION_AREAS.find(s => s.value === a)?.label) || a }));
  const pagedTrainers = filtered.slice((trainersPage - 1) * PAGE_SIZE, trainersPage * PAGE_SIZE);
  useEffect(() => { setTrainersPage(1); }, [search, colFilters, highlightFilter]);
  const availableTags = MICROSOFT_TECH_TAGS.filter(t => !form.expertise_tags.includes(t));
  const availableCerts = COMMON_CERT_CODES.filter(c => !form.certifications.includes(c)).slice(0, 16);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="trainers-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Trainers</h1>
          <p className="text-muted-foreground">Manage trainer roster and track delivery performance</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleCsvImport}
            className="hidden"
            data-testid="csv-file-input"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={importLoading} data-testid="import-trainer-btn">
                <Upload className="w-4 h-4 mr-2" />
                {importLoading ? 'Importing...' : 'Import CSV'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => fileInputRef.current?.click()} data-testid="upload-csv-option">
                <Upload className="w-4 h-4 mr-2" />
                Upload CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => trainersAPI.downloadCsvTemplate()} data-testid="download-template-option">
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={openAddDialog} className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="add-trainer-btn">
            <Plus className="w-4 h-4 mr-2" />Add Trainer
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-start">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search by name, email, expertise..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="search-trainers" />
        </div>
        <div className="flex items-center gap-2 rounded-md border px-3 py-1.5 bg-card">
          <ColumnFilterPopover label="Country" options={countryOpts} selected={colFilters.country} onChange={(s) => setColFilter('country', s)} testId="trainers-filter-country" />
          <span className="text-muted-foreground/40">|</span>
          <ColumnFilterPopover label="Solution Area" options={solutionAreaOpts} selected={colFilters.solution_area} onChange={(s) => setColFilter('solution_area', s)} testId="trainers-filter-solution" />
        </div>
      </div>

      <ActiveFiltersBar
        filters={[
          { key: 'country', label: 'Country', set: colFilters.country, options: countryOpts, onChange: (s) => setColFilter('country', s) },
          { key: 'solution_area', label: 'Solution Area', set: colFilters.solution_area, options: solutionAreaOpts, onChange: (s) => setColFilter('solution_area', s) },
        ]}
        search={search}
        onClearSearch={() => setSearch('')}
        testId="trainers-active-filters"
      />

      {/* Highlight Filters */}
      <div className="flex flex-wrap gap-2" data-testid="highlight-filters">
        {[
          { key: 'all', label: 'All Trainers' },
          { key: 'featured', label: 'Featured' },
          { key: 'top_rated', label: 'Top Rated' },
          { key: 'most_utilized', label: 'Most Utilized' },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setHighlightFilter(f.key)}
            data-testid={`highlight-filter-${f.key}`}
            className={`text-sm px-4 py-1.5 rounded-full border transition-colors font-medium ${
              highlightFilter === f.key
                ? 'bg-lime text-lime-foreground border-lime'
                : 'border-border bg-background hover:border-lime/50 text-muted-foreground'
            }`}
          >
            {f.key === 'featured' && <Bookmark className="w-3.5 h-3.5 inline mr-1.5" />}
            {f.key === 'top_rated' && <Star className="w-3.5 h-3.5 inline mr-1.5" />}
            {f.key === 'most_utilized' && <TrendingUp className="w-3.5 h-3.5 inline mr-1.5" />}
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1,2,3].map(i => <div key={i} className="h-52 bg-muted animate-pulse rounded-xl" />)}
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pagedTrainers.map(trainer => (
            <Card key={trainer.id} className={`hover:shadow-md transition-shadow ${!trainer.is_active ? 'opacity-60' : ''}`} data-testid={`trainer-card-${trainer.id}`}>
              <CardContent className="pt-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="relative cursor-pointer"
                      onClick={() => navigate(`/trainers/${trainer.id}`)}
                      title="View Profile"
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-blue-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 hover:ring-2 hover:ring-violet-400 transition-all">
                        {trainer.name.charAt(0).toUpperCase()}
                      </div>
                      {trainer.is_featured && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center">
                          <Bookmark className="w-2.5 h-2.5 text-white fill-white" />
                        </div>
                      )}
                    </div>
                    <div>
                      <h3
                        className="font-semibold leading-tight hover:text-primary cursor-pointer transition-colors"
                        onClick={() => navigate(`/trainers/${trainer.id}`)}
                      >
                        {trainer.name}
                      </h3>
                      {trainer.country && <p className="text-xs text-muted-foreground flex items-center gap-1"><Globe className="w-3 h-3" />{trainer.country}</p>}
                    </div>
                  </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal className="w-4 h-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/trainers/${trainer.id}`)}><User className="w-4 h-4 mr-2" />View Profile</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEditDialog(trainer)}><Edit2 className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleToggleFeatured(trainer)}>
                        <Bookmark className="w-4 h-4 mr-2" />
                        {trainer.is_featured ? 'Remove from Featured' : 'Mark as Featured'}
                      </DropdownMenuItem>
                      {trainer.is_active && <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleDeactivate(trainer)} className="text-destructive"><UserX className="w-4 h-4 mr-2" />Deactivate</DropdownMenuItem>
                      </>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="space-y-1.5 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /><span className="truncate">{trainer.email}</span></div>
                  {trainer.phone && <div className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{trainer.phone}</div>}
                </div>

                <div className="mt-3 flex items-center gap-4 text-sm">
                  <button
                    onClick={() => navigate(`/sessions?trainer=${encodeURIComponent(trainer.name)}`)}
                    className="flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors group"
                    data-testid={`trainer-sessions-link-${trainer.id}`}
                    title="View sessions for this trainer"
                  >
                    <CalendarDays className="w-3.5 h-3.5" />
                    <span className="group-hover:underline">{trainer.total_sessions || 0} sessions</span>
                  </button>
                  {trainer.avg_rating ? <StarRating value={trainer.avg_rating} /> : <span className="text-xs text-muted-foreground">No ratings yet</span>}
                </div>

                {(trainer.expertise_tags || []).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {trainer.expertise_tags.slice(0, 4).map(tag => <Badge key={tag} className="text-xs bg-blue-100 text-blue-800 font-normal">{tag}</Badge>)}
                    {trainer.expertise_tags.length > 4 && <Badge variant="outline" className="text-xs">+{trainer.expertise_tags.length - 4}</Badge>}
                  </div>
                )}
                {(trainer.certifications || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {trainer.certifications.slice(0, 3).map(cert => <Badge key={cert} variant="secondary" className="text-xs font-mono">{cert}</Badge>)}
                    {trainer.certifications.length > 3 && <Badge variant="outline" className="text-xs">+{trainer.certifications.length - 3}</Badge>}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No trainers found</h3>
          <p className="text-muted-foreground mb-4">{search ? 'Try adjusting your search.' : 'Add your first trainer to get started.'}</p>
          {!search && <Button onClick={openAddDialog} className="bg-lime text-lime-foreground hover:bg-lime/90"><Plus className="w-4 h-4 mr-2" />Add Trainer</Button>}
        </div>
      )}
      <TablePagination page={trainersPage} total={filtered.length} onPageChange={setTrainersPage} testId="trainers-pagination" itemLabel="trainers" />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Trainer' : 'Add New Trainer'}</DialogTitle>
            <DialogDescription>{editing ? 'Update trainer profile' : 'Add a trainer to your roster'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Full Name *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({...f,name:e.target.value}))} placeholder="Jane Doe" data-testid="trainer-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={e => setForm(f => ({...f,email:e.target.value}))} placeholder="jane@example.com" data-testid="trainer-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input value={form.phone} onChange={e => setForm(f => ({...f,phone:e.target.value}))} placeholder="+91 98765 43210" />
              </div>
              <div className="space-y-1.5">
                <Label>Country</Label>
                <Select value={form.country} onValueChange={v => setForm(f => ({...f,country:v}))}>
                  <SelectTrigger data-testid="trainer-country-select"><SelectValue placeholder="Select country..." /></SelectTrigger>
                  <SelectContent>{COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>Timezone</Label>
                <Input value={form.timezone} onChange={e => setForm(f => ({...f,timezone:e.target.value}))} placeholder="e.g. IST (UTC+5:30)" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Tag className="w-4 h-4" />Microsoft Technology Expertise</Label>
              <div className="flex flex-wrap gap-1.5 p-3 border rounded-lg min-h-[2.5rem] bg-muted/30">
                {form.expertise_tags.map(tag => (
                  <Badge key={tag} className="bg-blue-100 text-blue-800 gap-1 pr-1">
                    {tag}<button onClick={() => removeTag(tag)}><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
                <input className="flex-1 min-w-[120px] bg-transparent text-sm outline-none placeholder:text-muted-foreground" placeholder="Type tag + Enter..." value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key==='Enter'&&(e.preventDefault(),addTag(tagInput))} data-testid="tag-input" />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {availableTags.map(t => (
                  <button key={t} onClick={() => addTag(t)} className="text-xs px-2 py-0.5 rounded-full border border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 transition-colors">+ {t}</button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1.5"><Award className="w-4 h-4" />Certifications</Label>
              <div className="flex flex-wrap gap-1.5 p-3 border rounded-lg min-h-[2.5rem] bg-muted/30">
                {form.certifications.map(cert => (
                  <Badge key={cert} variant="secondary" className="font-mono gap-1 pr-1">
                    {cert}<button onClick={() => removeCert(cert)}><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
                <input className="flex-1 min-w-[100px] bg-transparent text-sm font-mono outline-none placeholder:text-muted-foreground" placeholder="AZ-104 + Enter..." value={certInput} onChange={e => setCertInput(e.target.value)} onKeyDown={e => e.key==='Enter'&&(e.preventDefault(),addCert(certInput))} data-testid="cert-input" />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {availableCerts.map(c => (
                  <button key={c} onClick={() => addCert(c)} className="text-xs px-2 py-0.5 rounded-full border border-dashed border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors font-mono">+ {c}</button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Solution Areas</Label>
              <div className="flex flex-wrap gap-2">
                {SOLUTION_AREAS.map(area => (
                  <button key={area.value} type="button" onClick={() => toggleSolutionArea(area.value)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.solution_areas.includes(area.value) ? 'bg-lime text-lime-foreground border-lime' : 'border-border hover:border-lime/50'}`}>
                    {area.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Bio / Notes</Label>
              <Textarea value={form.bio} onChange={e => setForm(f => ({...f,bio:e.target.value}))} placeholder="Background, specializations..." rows={2} />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="save-trainer-btn">
                {saving ? 'Saving...' : editing ? 'Update Trainer' : 'Add Trainer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainersPage;
